import { useEffect, useRef } from "react"

type Particle = {
  x: number
  y: number
  vx: number
  vy: number
  rotation: number
  vr: number
  w: number
  h: number
  color: string
  shape: "rect" | "circle" | "strip"
  life: number
  decay: number
}

const COLORS = ["#C8F542", "#9BE01A", "#5CFFEF", "#4FC3F7", "#FF8A3D", "#FF4FD8", "#FFFFFF", "#FFE56B"]

function spawnBurst(width: number, height: number): Particle[] {
  const particles: Particle[] = []
  const origins = [
    { x: width * 0.5, y: height * 0.42 },
    { x: width * 0.18, y: height * 0.22 },
    { x: width * 0.82, y: height * 0.22 },
  ]

  origins.forEach((origin, originIndex) => {
    const count = originIndex === 0 ? 140 : 70
    for (let i = 0; i < count; i += 1) {
      const angle = originIndex === 0
        ? Math.random() * Math.PI * 2
        : -Math.PI * 0.85 + Math.random() * Math.PI * 0.7
      const speed = 7 + Math.random() * 14
      particles.push({
        x: origin.x,
        y: origin.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - (originIndex === 0 ? 6 : 2),
        rotation: Math.random() * 360,
        vr: -8 + Math.random() * 16,
        w: 6 + Math.random() * 10,
        h: 8 + Math.random() * 14,
        color: COLORS[Math.floor(Math.random() * COLORS.length)] || "#C8F542",
        shape: i % 5 === 0 ? "circle" : i % 3 === 0 ? "strip" : "rect",
        life: 1,
        decay: 0.006 + Math.random() * 0.01,
      })
    }
  })

  return particles
}

type ConfettiBurstProps = {
  burstId: number
}

export default function ConfettiBurst({ burstId }: ConfettiBurstProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const particlesRef = useRef<Particle[]>([])
  const frameRef = useRef<number>(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    const context = canvas.getContext("2d")
    if (!context) return undefined

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    resize()
    window.addEventListener("resize", resize)

    const tick = () => {
      context.clearRect(0, 0, canvas.width, canvas.height)
      const next: Particle[] = []

      particlesRef.current.forEach((particle) => {
        particle.vy += 0.28
        particle.vx *= 0.992
        particle.x += particle.vx
        particle.y += particle.vy
        particle.rotation += particle.vr
        particle.life -= particle.decay

        if (particle.life <= 0 || particle.y > canvas.height + 40) return

        context.save()
        context.globalAlpha = Math.max(particle.life, 0)
        context.translate(particle.x, particle.y)
        context.rotate((particle.rotation * Math.PI) / 180)
        context.fillStyle = particle.color

        if (particle.shape === "circle") {
          context.beginPath()
          context.arc(0, 0, particle.w * 0.45, 0, Math.PI * 2)
          context.fill()
        } else if (particle.shape === "strip") {
          context.fillRect(-particle.w * 0.15, -particle.h, particle.w * 0.3, particle.h * 1.4)
        } else {
          context.fillRect(-particle.w / 2, -particle.h / 2, particle.w, particle.h)
        }

        context.restore()
        next.push(particle)
      })

      particlesRef.current = next
      frameRef.current = window.requestAnimationFrame(tick)
    }

    frameRef.current = window.requestAnimationFrame(tick)

    return () => {
      window.removeEventListener("resize", resize)
      window.cancelAnimationFrame(frameRef.current)
    }
  }, [])

  useEffect(() => {
    if (burstId <= 0) return
    const canvas = canvasRef.current
    if (!canvas) return
    particlesRef.current = [
      ...particlesRef.current,
      ...spawnBurst(canvas.width || window.innerWidth, canvas.height || window.innerHeight),
    ]
  }, [burstId])

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-50"
      aria-hidden="true"
    />
  )
}
